import { useEffect, useRef } from 'react';
import { type RealtimeChannel, type RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabaseClient';
import { executionKeys } from '../keys';
import type { ExecutionDetailsPayload, ExecutionTimelineEntry } from '../../../services/executionService';

const channelRegistry = new Map<string, RealtimeChannel>();
const channelOrder: string[] = [];
const MAX_CHANNELS = 3;

function registerChannel(key: string, channel: RealtimeChannel) {
  channelRegistry.set(key, channel);
  channelOrder.push(key);
  trimChannels();
}

function trimChannels() {
  while (channelOrder.length > MAX_CHANNELS) {
    const oldestKey = channelOrder.shift();
    if (!oldestKey) continue;
    const channel = channelRegistry.get(oldestKey);
    channel?.unsubscribe();
    channelRegistry.delete(oldestKey);
  }
}

function deregisterChannel(key: string) {
  const channel = channelRegistry.get(key);
  if (channel) {
    channel.unsubscribe();
    channelRegistry.delete(key);
  }
  const index = channelOrder.indexOf(key);
  if (index >= 0) {
    channelOrder.splice(index, 1);
  }
}

type ExecutionLogRow = {
  id: string;
  event_type: string;
  metadata?: Record<string, any> | null;
  created_at: string;
};

function mapExecutionLogPayload(payload: RealtimePostgresChangesPayload<Record<string, any>>): ExecutionTimelineEntry | null {
  const row = payload.new as ExecutionLogRow | null;
  if (!row) return null;
  return {
    id: String(row.id),
    action: row.event_type,
    actionDescription: row.event_type === 'EXECUTION_REPROCESSED' ? 'Execução reprocessada' : row.event_type,
    justification: row.metadata?.reason ?? null,
    performedByName: row.metadata?.actor_name ?? null,
    performedByRole: row.metadata?.actor_role ?? undefined,
    timestamp: row.created_at,
    stageName: null,
    metadata: row.metadata ?? null,
  };
}

export function useExecutionRealtime(executionId?: string | null, organizationId?: string | null, enabled = false) {
  const queryClient = useQueryClient();

  // Stable ref so the effect never re-runs due to callback identity changes
  const stableRef = useRef({ executionId, organizationId, queryClient });
  stableRef.current = { executionId, organizationId, queryClient };

  useEffect(() => {
    if (!enabled || !executionId || !organizationId) {
      return undefined;
    }

    const channelKey = `execution-${organizationId}-${executionId}`;

    // If a channel already exists for this exact key, skip creating a duplicate
    if (channelRegistry.has(channelKey)) {
      return () => {
        deregisterChannel(channelKey);
      };
    }

    const channel = supabase
      .channel(channelKey)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'execution_logs',
          filter: `execution_id=eq.${executionId}`,
        },
        (payload: RealtimePostgresChangesPayload<Record<string, any>>) => {
          const { executionId: eid, organizationId: oid, queryClient: qc } = stableRef.current;
          if (!oid || !eid) return;
          const entry = mapExecutionLogPayload(payload);
          if (!entry) return;
          const key = executionKeys.detail(oid, eid);
          qc.setQueryData<ExecutionDetailsPayload | undefined>(key, (current) => {
            if (!current) return current;
            if (current.timeline.some((item) => item.id === entry.id)) return current;
            const nextTimeline = [entry, ...current.timeline].sort(
              (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
            );
            return { ...current, timeline: nextTimeline };
          });
        },
      )
      .subscribe();

    registerChannel(channelKey, channel);

    return () => {
      deregisterChannel(channelKey);
    };
  }, [enabled, executionId, organizationId]);
}
